import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getActiveCampaigns,
  getRecentCampaigns,
  stopCampaign,
  getDraftedCampaign,
  deleteCampaign,
} from "./campaignService";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { FiArrowLeft } from "react-icons/fi";

export default function CampaignsList() {
  const navigate = useNavigate();
  const location = useLocation();

  // Active campaigns state
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [loadingActive, setLoadingActive] = useState(true);
  const [activeError, setActiveError] = useState("");

  // Drafted campaigns state
  const [draftedCampaign, setDraftedCampaign] = useState(null);
  const [loadingDrafted, setLoadingDrafted] = useState(true);
  const [draftedError, setDraftedError] = useState("");

  // Recent campaigns state
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [recentError, setRecentError] = useState("");

  // Stop campaign modal state
  const [showStopModal, setShowStopModal] = useState(false);
  const [stoppingCampaign, setStoppingCampaign] = useState(false);
  // Delete campaign modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);

  // Fetch active campaigns
  const fetchActiveCampaign = async () => {
    setLoadingActive(true);
    setActiveError("");
    try {
      const response = await getActiveCampaigns();
      // Extract single active campaign object
      const campaign =
        response?.body?.activeCampaign || response?.activeCampaign || null;

      // Check if campaign is an empty object or has no _id/id
      if (
        campaign &&
        typeof campaign === "object" &&
        Object.keys(campaign).length === 0
      ) {
        setActiveCampaign(null);
      } else if (campaign && !campaign._id && !campaign.id) {
        setActiveCampaign(null);
      } else {
        setActiveCampaign(campaign);
      }
    } catch (err) {
      console.error("Failed to load active campaigns:", err);
      setActiveError("Failed to load active campaigns");
    } finally {
      setLoadingActive(false);
    }
  };

  // Fetch recent campaigns
  const fetchRecentCampaigns = async () => {
    setLoadingRecent(true);
    setRecentError("");
    try {
      const response = await getRecentCampaigns();
      // Handle both array and nested response structures
      const campaigns = Array.isArray(response?.body)
        ? response.body
        : response?.body?.campaigns || response?.campaigns || [];
      setRecentCampaigns(campaigns);
    } catch (err) {
      console.error("Failed to load recent campaigns:", err);
      setRecentError("Failed to load recent campaigns");
    } finally {
      setLoadingRecent(false);
    }
  };

  // Fetch drafted campaign
  // Fetch drafted campaign
  const fetchDraftedCampaign = async () => {
    setLoadingDrafted(true);
    setDraftedError("");
    try {
      const response = await getDraftedCampaign();
      const draft =
        response?.body?.draftedCampaign || response?.draftedCampaign || null;

      // Check if draft is an empty object or has no _id/id
      if (
        draft &&
        typeof draft === "object" &&
        Object.keys(draft).length === 0
      ) {
        setDraftedCampaign(null);
      } else if (draft && !draft._id && !draft.id) {
        setDraftedCampaign(null);
      } else {
        setDraftedCampaign(draft);
      }
    } catch (err) {
      console.error("Failed to load drafted campaign:", err);
      setDraftedError("Failed to load drafted campaign");
    } finally {
      setLoadingDrafted(false);
    }
  };

  useEffect(() => {
    fetchActiveCampaign();
  }, []);

  useEffect(() => {
    fetchRecentCampaigns();
  }, []);

  useEffect(() => {
    fetchDraftedCampaign();
  }, []);

  // Refetch when navigating back to this page
  useEffect(() => {
    // Check if we're coming back from campaign creation
    if (location.state?.refetch) {
      fetchActiveCampaign();
      fetchRecentCampaigns();
      fetchDraftedCampaign();
      // Clear the state to prevent refetching on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Get badges for campaign
  const getCampaignBadges = (campaign) => {
    const badges = [];
    if (campaign.isEmail)
      badges.push({ label: "Email", color: "bg-[#0d7ac2] text-white" });
    if (campaign.isSms)
      badges.push({ label: "SMS", color: "bg-[#0d7ac2] text-white" });
    if (campaign.status === "scheduled")
      badges.push({ label: "Scheduled", color: "bg-gray-300 text-gray-800" });
    if (campaign.status === "draft")
      badges.push({ label: "Draft", color: "bg-gray-300 text-gray-800" });
    if (campaign.status === "sent")
      badges.push({ label: "Sent", color: "bg-green-300 text-green-800" });
    return badges.length > 0
      ? badges
      : [{ label: "Pending", color: "bg-gray-300 text-gray-800" }];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Calculate progress percentage
  const getProgressPercent = (campaign) => {
    if (!campaign || campaign.recipientsTotal === 0) return 0;
    return Math.round(
      (campaign.recipientsSent / campaign.recipientsTotal) * 100,
    );
  };

  // Get campaign type badges
  const getCampaignTypes = (campaign) => {
    const types = [];
    if (campaign?.isEmail) types.push("Email");
    if (campaign?.isSms) types.push("SMS");
    return types.length > 0 ? types.join(" + ") : "N/A";
  };

  // Handle stop campaign
  // Handle stop campaign
  const handleStopCampaign = async () => {
    if (!activeCampaign?._id) return;

    setStoppingCampaign(true);
    try {
      await stopCampaign(activeCampaign._id);
      // Refetch active campaign to update UI
      await fetchActiveCampaign();
      // Also refetch recent campaigns as the stopped campaign should appear there
      await fetchRecentCampaigns();
    } catch (err) {
      console.error("Failed to stop campaign:", err);
      alert("Failed to stop campaign. Please try again.");
    } finally {
      // Always close modal and reset stopping state
      setStoppingCampaign(false);
      setShowStopModal(false);
    }
  };

  // Handle delete campaign
  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    const id = campaignToDelete?._id || campaignToDelete?.id;
    if (!id) return;

    setDeletingCampaign(true);
    try {
      await deleteCampaign(id);
      // Refetch lists
      await fetchDraftedCampaign();
      await fetchActiveCampaign();
      await fetchRecentCampaigns();
      setShowDeleteModal(false);
      setCampaignToDelete(null);
    } catch (err) {
      console.error("Failed to delete campaign:", err);
      alert("Failed to delete campaign. Please try again.");
    } finally {
      setDeletingCampaign(false);
    }
  };

  // Skeleton loader for active campaign
  const ActiveCampaignSkeleton = () => (
    <div className="bg-white w-full border border-gray-200 rounded-xl shadow-sm p-4 md:p-6 animate-pulse">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full bg-gray-300" />
        <div className="h-4 w-32 bg-gray-300 rounded" />
      </div>
      <div className="h-5 w-3/4 bg-gray-300 rounded mb-1" />
      <div className="h-3 w-1/2 bg-gray-300 rounded mb-3" />
      <div className="flex items-center gap-3">
        <div className="flex-1 pt-3">
          <div className="w-full h-2 bg-gray-300 rounded mb-1" />
          <div className="flex items-center justify-between">
            <div className="h-3 w-24 bg-gray-300 rounded" />
            <div className="h-3 w-8 bg-gray-300 rounded" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-8 w-16 bg-gray-300 rounded" />
          <div className="h-8 w-16 bg-gray-300 rounded" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/admin/marketing")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to Marketing"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-semibold mb-2">Campaigns</h1>
          <p className="text-gray-600">Create and send email campaigns</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Active Campaign Card */}
        {loadingActive ? (
          <ActiveCampaignSkeleton />
        ) : activeError ? (
          <div className="bg-red-50 w-full border border-red-200 rounded-xl shadow-sm p-4 md:p-6 flex items-center justify-center">
            <p className="text-red-600 text-sm">{activeError}</p>
          </div>
        ) : activeCampaign ? (
          <div className="bg-white w-full border border-gray-200 rounded-xl shadow-sm p-4 md:p-6">
            <div className="flex items-center gap-2 text-sm text-green-700 mb-2">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span>Active Campaign</span>
            </div>
            <p className="text-gray-900 font-medium mb-1 line-clamp-2">
              "{activeCampaign.campaignName || "N/A"}"
            </p>
            <p className="text-xs text-gray-500 mb-1">
              {"Launched On: " +
                (activeCampaign.scheduledTime
                  ? formatDate(activeCampaign.scheduledTime)
                  : formatDate(activeCampaign.created_at))}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex-1 pt-3">
                <div className="w-full h-2 bg-gray-200 rounded">
                  <div
                    className="h-2 bg-[#0d7ac2] rounded"
                    style={{ width: `${getProgressPercent(activeCampaign)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">
                    Sent: {activeCampaign.recipientsSent || 0}/
                    {activeCampaign.recipientsTotal || 0}
                  </span>
                  <span className="text-xs text-gray-500">
                    {getProgressPercent(activeCampaign)}%
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    navigate(
                      `/admin/marketing/campaigns/${activeCampaign._id || activeCampaign.id}`,
                    )
                  }
                  className="px-3 py-1 text-sm rounded bg-[#0d7ac2] text-white hover:bg-[#0a69a6] transition-colors whitespace-nowrap"
                >
                  View
                </button>
                <button
                  onClick={() => setShowStopModal(true)}
                  className="px-3 py-1 text-sm rounded bg-[#a44646] text-white hover:bg-[#8a3a3a] transition-colors whitespace-nowrap"
                >
                  Stop
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 w-full border border-gray-200 rounded-xl shadow-sm p-4 md:p-6 flex items-center justify-center">
            <p className="text-gray-500 text-sm">No active campaigns</p>
          </div>
        )}

        {/* Create New Campaign Card */}
        <div className="bg-white flex items-center h-[100px] border border-gray-200 rounded-xl shadow-sm p-4 w-full">
          <button
            onClick={() => navigate("/admin/marketing/campaign-create")}
            className="w-full rounded-md bg-[#0d7ac2] hover:bg-[#0a69a6] text-white py-3 transition-colors"
          >
            Create New
          </button>
        </div>
      </div>

      {/* Drafted Campaign Section */}
      {!loadingDrafted && !draftedError && draftedCampaign && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium">Drafted Campaigns</h3>
          </div>

          {/*** Drafted Campaign Card ***/}
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <p className="text-gray-900 font-medium line-clamp-1">
                  {draftedCampaign.campaignName ||
                    draftedCampaign.title ||
                    "Untitled Campaign"}
                </p>
                <div className="flex items-center gap-2">
                  {getCampaignBadges(draftedCampaign).map((b, i) => (
                    <span
                      key={i}
                      className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${b.color}`}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                {formatDate(draftedCampaign.created_at)} •{" "}
                {draftedCampaign.recipientCount ||
                  draftedCampaign.recipients ||
                  0}{" "}
                recipients
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={() =>
                  navigate("/admin/marketing/campaign-create", {
                    state: { draftCampaign: draftedCampaign },
                  })
                }
                className="text-sm text-[#0d7ac2] hover:underline whitespace-nowrap ml-4"
              >
                Edit
              </button>
              <button
                onClick={() => handleDeleteClick(draftedCampaign)}
                className="text-sm text-red-600 hover:underline whitespace-nowrap ml-4"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Campaigns Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium">Recent Campaigns</h3>
        </div>

        {loadingRecent ? (
          <div className="text-center py-8 text-gray-500">
            Loading recent campaigns...
          </div>
        ) : recentError ? (
          <div className="text-center py-8 text-red-600">{recentError}</div>
        ) : recentCampaigns.length > 0 ? (
          <div className="space-y-3">
            {recentCampaigns.map((campaign) => (
              <div
                key={campaign._id || campaign.id}
                className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <p className="text-gray-900 font-medium line-clamp-1">
                      {campaign.campaignName ||
                        campaign.title ||
                        "Untitled Campaign"}
                    </p>
                    <div className="flex items-center gap-2">
                      {getCampaignBadges(campaign).map((b, i) => (
                        <span
                          key={i}
                          className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${b.color}`}
                        >
                          {b.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    {formatDate(campaign.created_at)} •{" "}
                    {campaign.recipientCount || campaign.recipients || 0}{" "}
                    recipients
                  </p>
                </div>
                <button
                  onClick={() =>
                    navigate(
                      `/admin/marketing/campaigns/${campaign._id || campaign.id}`,
                    )
                  }
                  className="text-sm text-[#0d7ac2] hover:underline whitespace-nowrap ml-4"
                >
                  View
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No recent campaigns found
          </div>
        )}
      </div>

      {/* Stop Campaign Modal */}
      {showStopModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Stop Campaign?
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to stop this campaign? This action cannot be
              undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowStopModal(false)}
                disabled={stoppingCampaign}
                className="flex-1 px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                No
              </button>
              <button
                onClick={handleStopCampaign}
                disabled={stoppingCampaign}
                className="flex-1 px-4 py-2 rounded bg-[#a44646] text-white hover:bg-[#8a3a3a] transition-colors disabled:opacity-50"
              >
                {stoppingCampaign ? "Stopping..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Campaign Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={deletingCampaign}
      />
    </div>
  );
}
